import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Map, List } from 'immutable';

import AgGridTable from '../../helpers/AgGridTable';
import TextAreaEditor from '../TextAreaEditor';
import { handleSortModelChanged } from '../../../utils/sorting';

import style from './style.module.css';

const MailingInfoTable = ({ columnDefs, data, onChange, onDoubleClickRow, onGetNextPageData, onUpdateCell }) => {
  const [gridApi, setGridApi] = useState(null);
  const columnWidthRef = {};

  const lastColumnDefsRef = useRef(null);
  const columnDefsRef = useRef(null);
  const containerRef = useRef(null);

  const getColumnDefs = useCallback(() => {
    if (columnDefs !== lastColumnDefsRef.current) {
      lastColumnDefsRef.current = columnDefs;
      columnDefsRef.current = columnDefs.map(columnDef => {
        columnWidthRef[columnDef.get('field')] = columnDef.get('width');

        return columnDef.merge({
          autoHeight: true,
          cellEditorFramework: TextAreaEditor,
        });
      });
    }

    return columnDefsRef.current;
  }, [columnDefs]);

  const handleGridReady = useCallback(params => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
    params.api.showLoadingOverlay();
  }, []);

  const handleCellClicked = useCallback(
    data => {
      gridApi.startEditingCell({
        rowIndex: data.node.rowIndex,
        colKey: data.colDef.field,
      });
    },
    [gridApi],
  );

  const handleCellValueChanged = useCallback(
    event => {
      if (event.newValue !== event.oldValue) {
        const { updateType, updateField } = event.colDef;

        onUpdateCell({
          updateType,
          updateField,
          newValue: event.newValue,
          data: event.data,
        });
      }
    },
    [onUpdateCell],
  );

  const handleSort = useCallback(
    sortModel => {
      handleSortModelChanged(sortModel, data, onChange);
    },
    [data, onChange],
  );

  const handleClickOutside = useCallback(
    event => {
      if (containerRef.current && containerRef.current.contains(event.target)) {
        return;
      }

      gridApi?.stopEditing();
    },
    [gridApi],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (gridApi) {
      gridApi.resetRowHeights();
    }
  }, [data, gridApi]);

  const getRowNodeId = useCallback(data => data.index, []);

  return (
    <div ref={containerRef} className={style.host}>
      <AgGridTable
        columnDefs={getColumnDefs()}
        getRowNodeId={getRowNodeId}
        isFetching={data.get('isFetching')}
        onCellClicked={handleCellClicked}
        onCellValueChanged={handleCellValueChanged}
        onGetNextPageData={onGetNextPageData}
        onGridReady={handleGridReady}
        onRowDoubleClicked={onDoubleClickRow}
        onSortModelChanged={handleSort}
        page={data.get('page')}
        rowData={data.get('queryResults')}
        totalPages={data.get('totalPages')}
        sortable
      />
    </div>
  );
};

MailingInfoTable.propTypes = {
  columnDefs: PropTypes.instanceOf(List).isRequired,
  data: PropTypes.instanceOf(Map).isRequired,
  onChange: PropTypes.func.isRequired,
  onDoubleClickRow: PropTypes.func.isRequired,
  onGetNextPageData: PropTypes.func.isRequired,
  onUpdateCell: PropTypes.func.isRequired,
};

export default MailingInfoTable;
